import { Controller } from '@hotwired/stimulus';
import 'chart.js/dist/chart.min';

export default class extends Controller {
  static targets = ['canvas', 'tooltip'];

  static values = {
    chartType: String,
    labels: Array,
    data: Array,
    backgroundColor: Array,
    borderColor: Array,
    options: Object,
  };

  connect() {
    this.initChart();

    this.abortController = new AbortController();
    document.addEventListener('shown.bs.tab', (event) => {
      const tabId = event.target.getAttribute('href');
      const tab = document.querySelector(tabId);

      if (tab && tab.contains(this.element)) {
        this.initChart();
      }
    }, { signal: this.abortController.signal });
  }

  disconnect() {
    this.abortController.abort();

    const existingChart = Chart.getChart(this.canvasTarget);
    if (existingChart) {
      existingChart.destroy();
    }
  }

  initChart() {
    const existingChart = Chart.getChart(this.canvasTarget);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = this.canvasTarget.getContext('2d');
    const chartType = this.chartTypeValue;
    const isPie = chartType === 'pie';

    const chartOptions = {
      type: chartType,
      data: {
        labels: this.labelsValue,
        datasets: [{
          label: this.optionsValue.label,
          data: this.dataValue,
          backgroundColor: this.backgroundColorValue,
          borderColor: this.borderColorValue,
          borderWidth: isPie ? 0 : 1,
        }],
      },
      options: {
        plugins: {
          tooltip: isPie
            ? { enabled: false, external: (context) => this.#renderPieTooltip(context) }
            : { callbacks: { label: (tooltipItem) => tooltipItem.raw } },
          legend: {
            display: isPie ? false : chartType !== 'bar',
            position: 'bottom',
          },
        },
        responsive: !isPie,
        maintainAspectRatio: false,
      },
    };

    if (!isPie) {
      chartOptions.options.scales = {
        x: {
          title: { display: true, text: this.optionsValue.xLabel },
        },
        y: {
          title: { display: true, text: this.optionsValue.yLabel },
          beginAtZero: true,
        },
      };
    }

    new Chart(ctx, chartOptions);
  }

  #renderPieTooltip(context) {
    const { chart, tooltip } = context;

    this.tooltipTargets.forEach((tooltipElement) => tooltipElement.classList.add('d-none'));

    if (tooltip.opacity === 0) {
      return;
    }

    const tooltipElement = this.tooltipTargets[tooltip.dataPoints[0].dataIndex];
    if (!tooltipElement) {
      return;
    }

    tooltipElement.classList.remove('d-none');
    tooltipElement.style.left = `${chart.canvas.offsetLeft + tooltip.caretX}px`;
    tooltipElement.style.top = `${chart.canvas.offsetTop + tooltip.caretY}px`;
  }
}
